import React from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
} from '@mui/material';
import { useTranslation } from '../../../app/LanguageContext';
import { subtleSurfaceBg } from '../../../theme/surfaceSx';

export default function VariantListToolbar({
  options = [],
  searchQuery = '',
  onSearchChange,
  filters = {},
  onFilterChange,
  groupByIndex = 0,
  onGroupByChange,
  filteredCount = 0,
  totalCount = 0,
  hasActiveFilters = false,
  showGroupControls = false,
  allGroupsExpanded = false,
  onExpandAllGroups,
  onCollapseAllGroups,
}) {
  const { translate } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.25,
        alignItems: 'center',
        p: 1.5,
        bgcolor: subtleSurfaceBg,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
      }}
    >
      <TextField
        type="search"
        size="small"
        placeholder={translate('products.variant_search')}
        value={searchQuery}
        onChange={(e) => onSearchChange?.(e.target.value)}
        sx={{ minWidth: 180, flex: 1 }}
      />
      {options.map((opt) => (
        <FormControl key={opt.key || opt.name} size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{opt.name}</InputLabel>
          <Select
            label={opt.name}
            value={filters[opt.key] ?? filters[opt.name] ?? ''}
            onChange={(e) => onFilterChange?.(opt.key, e.target.value)}
          >
            <MenuItem value="">
              {translate('products.variant_filter_all').replace('{option}', opt.name)}
            </MenuItem>
            {(opt.values || []).map((val) => (
              <MenuItem key={val} value={val}>
                {val}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ))}
      {options.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            {translate('products.variant_group_by')}:
          </Typography>
          {options.map((opt, idx) => (
            <Chip
              key={opt.key || opt.name}
              label={opt.name}
              size="small"
              variant={groupByIndex === idx ? 'filled' : 'outlined'}
              color={groupByIndex === idx ? 'primary' : 'default'}
              onClick={() => onGroupByChange?.(idx)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
          {showGroupControls && (
            <Button
              size="small"
              variant="text"
              onClick={() =>
                allGroupsExpanded ? onCollapseAllGroups?.() : onExpandAllGroups?.()
              }
              sx={{ minWidth: 0, px: 1, textTransform: 'none' }}
            >
              {allGroupsExpanded
                ? translate('products.variant_collapse_all')
                : translate('products.variant_expand_all')}
            </Button>
          )}
        </Box>
      )}
      {options.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
          {hasActiveFilters || searchQuery
            ? `${filteredCount} / ${totalCount}`
            : translate('products.variant_group_count').replace('{count}', String(totalCount))}
        </Typography>
      )}
    </Box>
  );
}
